/**
 * Durable last-resort store for appraisal completions.
 *
 * The offline sync queue lives in IndexedDB. On iOS/WKWebView that connection
 * can be closed underneath us while the app is backgrounded, and a write made
 * on a stale handle is lost with no error the caller can see. When that
 * happened the completion never existed anywhere, so it could never replay —
 * the job stayed active forever.
 *
 * This mirror is written to localStorage (a different, synchronous storage
 * engine) at the moment the driver finishes. The sync engine rehydrates it
 * into the IndexedDB queue on every pass, and clears it once the completion
 * is queued or accepted by the server.
 *
 * Keys are deterministic (job + type) so a replay can never create duplicates.
 */

const KEY = "tm_completion_fallback";

export interface CompletionFallbackEntry {
  /** `${jobId}:${type}` — deterministic, dedupes replays */
  id: string;
  jobId: string;
  companyId: string;
  type: "COMPLETE_POC" | "COMPLETE_POD";
  payload_json: string;
  createdAt: number;
}

function readAll(): CompletionFallbackEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(entries: CompletionFallbackEntry[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    /* storage unavailable — nothing else we can do */
  }
}

export function saveCompletionFallback(
  entry: Omit<CompletionFallbackEntry, "id" | "createdAt">,
): string {
  const id = `${entry.jobId}:${entry.type}`;
  const entries = readAll().filter((e) => e.id !== id);
  entries.push({ ...entry, id, createdAt: Date.now() });
  writeAll(entries);
  return id;
}

export function getCompletionFallbacks(): CompletionFallbackEntry[] {
  return readAll();
}

export function removeCompletionFallback(id: string): void {
  writeAll(readAll().filter((e) => e.id !== id));
}

export function getCompletionFallbackCount(): number {
  return readAll().length;
}
